import type { PnlMonth } from '../api/schemas';
import { formatMoneyCents } from '../lib/format';

const MONTH_SHORT_ES = [
  'ene',
  'feb',
  'mar',
  'abr',
  'may',
  'jun',
  'jul',
  'ago',
  'sep',
  'oct',
  'nov',
  'dic',
] as const;

/** 'YYYY-MM' -> 'ene 26' (year only when the series crosses a year boundary). */
function monthLabel(month: string, withYear: boolean): string {
  const [year = '', monthNumber = ''] = month.split('-');
  const name = MONTH_SHORT_ES[Number(monthNumber) - 1] ?? month;
  return withYear ? `${name} ${year.slice(2)}` : name;
}

type PnlChartProps = {
  months: PnlMonth[];
};

/**
 * Ingresos vs gastos by month: paired thin columns on a hairline baseline,
 * legend up top, exact values in the table below (the chart is the shape,
 * the table is the number). Colors validated for CVD in both themes.
 */
export function PnlChart({ months }: PnlChartProps) {
  if (months.length === 0) return null;

  const maxCents = Math.max(1, ...months.flatMap((m) => [m.revenueCents, m.expenseCents]));
  const crossesYears = new Set(months.map((m) => m.month.slice(0, 4))).size > 1;
  // Cap at 94% so the tallest mark keeps headroom under the legend row.
  const heightPercent = (cents: number) => Math.max(cents > 0 ? 2 : 0, (cents / maxCents) * 94);

  return (
    <div className="pnl-chart-card">
      <div className="pnl-chart-head">
        <h3 className="pos-pane-label">Por mes</h3>
        <div className="pnl-legend" aria-hidden="true">
          <span className="entry">
            <span className="chip ingresos" />
            Ingresos
          </span>
          <span className="entry">
            <span className="chip gastos" />
            Gastos
          </span>
        </div>
      </div>

      <div className="pnl-chart" aria-hidden="true">
        {months.map((month) => (
          <div key={month.month} className="pnl-col">
            <div className="pnl-bars">
              <span
                className="pnl-bar ingresos"
                style={{ height: `${heightPercent(month.revenueCents)}%` }}
                data-value={`Ingresos ${formatMoneyCents(month.revenueCents)}`}
              />
              <span
                className="pnl-bar gastos"
                style={{ height: `${heightPercent(month.expenseCents)}%` }}
                data-value={`Gastos ${formatMoneyCents(month.expenseCents)}`}
              />
            </div>
            <p className="pnl-month-label">{monthLabel(month.month, crossesYears)}</p>
          </div>
        ))}
      </div>

      <div className="table-scroll pnl-table">
        <table>
          <thead>
            <tr>
              <th scope="col">Mes</th>
              <th scope="col" className="amount-col">
                Ingresos
              </th>
              <th scope="col" className="amount-col">
                Gastos
              </th>
              <th scope="col" className="amount-col">
                Neto
              </th>
            </tr>
          </thead>
          <tbody>
            {months.map((month) => (
              <tr key={month.month} data-month={month.month}>
                <th scope="row">{monthLabel(month.month, true)}</th>
                <td className="amount-cell">{formatMoneyCents(month.revenueCents)}</td>
                <td className="amount-cell">{formatMoneyCents(month.expenseCents)}</td>
                <td className="amount-cell" data-negative={month.netCents < 0 || undefined}>
                  {formatMoneyCents(month.netCents)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
